import { MapPin, MessageCircle, Zap, Shield, Search, Star } from 'lucide-react';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <Nav />
      <Hero />
      <Features />
      <HowItWorks />
      <Download />
      <Footer />
    </div>
  );
}

function Nav() {
  return (
    <header className="sticky top-0 z-50 border-b border-gray-100 bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <span className="text-xl font-bold text-indigo-600">Lambada</span>
        <nav className="flex items-center gap-6 text-sm text-gray-600">
          <a href="#features" className="hover:text-indigo-600 transition-colors">Features</a>
          <a href="#how-it-works" className="hover:text-indigo-600 transition-colors">How it works</a>
          <a href="/privacy" className="hover:text-indigo-600 transition-colors">Privacy</a>
        </nav>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="bg-gradient-to-br from-indigo-600 to-indigo-800 py-24 text-center px-6">
      <div className="mx-auto max-w-3xl">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-indigo-500/30 px-4 py-1.5 text-sm text-indigo-100">
          <MapPin className="h-3.5 w-3.5" />
          Hyperlocal service marketplace
        </div>
        <h1 className="mb-6 text-4xl font-bold leading-tight text-white sm:text-5xl">
          Find local services<br />right near you
        </h1>
        <p className="mb-10 text-lg text-indigo-200 max-w-xl mx-auto">
          Lambada connects you with local service providers and sellers within your neighborhood
          — real-time availability, direct connections, total privacy.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="https://apps.apple.com"
            className="inline-flex items-center justify-center gap-3 rounded-xl bg-white px-6 py-3.5 font-semibold text-indigo-700 shadow hover:bg-indigo-50 transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            <AppleIcon />
            App Store
          </a>
          <a
            href="https://play.google.com"
            className="inline-flex items-center justify-center gap-3 rounded-xl bg-indigo-500 px-6 py-3.5 font-semibold text-white shadow hover:bg-indigo-400 transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            <GooglePlayIcon />
            Google Play
          </a>
        </div>
      </div>
    </section>
  );
}

function Features() {
  const features = [
    {
      icon: <MapPin className="h-6 w-6 text-indigo-600" />,
      title: 'Hyperlocal discovery',
      desc: 'Find providers within 1–25 km. Adjust the radius to match your needs.',
    },
    {
      icon: <Zap className="h-6 w-6 text-indigo-600" />,
      title: 'Real-time availability',
      desc: 'See who is available right now. No more waiting for callbacks.',
    },
    {
      icon: <MessageCircle className="h-6 w-6 text-indigo-600" />,
      title: 'Direct messaging',
      desc: 'Chat 1-on-1 or in groups. Arrange everything without leaving the app.',
    },
    {
      icon: <Star className="h-6 w-6 text-indigo-600" />,
      title: 'Peer trust ratings',
      desc: 'Community-driven ratings are the sole trust signal — no platform gatekeeping.',
    },
    {
      icon: <Search className="h-6 w-6 text-indigo-600" />,
      title: 'Tag-based search',
      desc: 'Search by keyword or tag. Find exactly the skill you need, instantly.',
    },
    {
      icon: <Shield className="h-6 w-6 text-indigo-600" />,
      title: 'Privacy first',
      desc: 'Your precise location is never shown to others. Auto-deletion keeps your data off our servers.',
    },
  ];

  return (
    <section id="features" className="py-20 px-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-14 text-center">
          <h2 className="text-3xl font-bold text-gray-900">Why Lambada?</h2>
          <p className="mt-3 text-gray-500">Built for local businesses and everyday customers alike.</p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <div key={f.title} className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50">
                {f.icon}
              </div>
              <h3 className="mb-2 font-semibold text-gray-900">{f.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      step: '01',
      title: 'Create your profile',
      desc: 'Sign up with your phone number, Google, or Apple. Add your services, photos, and availability.',
    },
    {
      step: '02',
      title: 'Get discovered nearby',
      desc: 'Your profile appears to customers searching within your area. Toggle your availability status anytime.',
    },
    {
      step: '03',
      title: 'Chat and connect',
      desc: 'Customers message you directly. Arrange the details however works best for both of you.',
    },
  ];

  return (
    <section id="how-it-works" className="bg-gray-50 py-20 px-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-14 text-center">
          <h2 className="text-3xl font-bold text-gray-900">How it works</h2>
          <p className="mt-3 text-gray-500">Up and running in minutes.</p>
        </div>
        <div className="space-y-8">
          {steps.map((s) => (
            <div key={s.step} className="flex gap-6 rounded-2xl bg-white p-6 shadow-sm">
              <div className="flex-shrink-0 text-3xl font-bold text-indigo-100">{s.step}</div>
              <div>
                <h3 className="mb-1 font-semibold text-gray-900">{s.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Download() {
  return (
    <section className="py-20 px-6 text-center">
      <div className="mx-auto max-w-xl">
        <h2 className="mb-4 text-3xl font-bold text-gray-900">Ready to get started?</h2>
        <p className="mb-8 text-gray-500">Download the app and discover what's around you.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="https://apps.apple.com"
            className="inline-flex items-center justify-center gap-3 rounded-xl bg-indigo-600 px-6 py-3.5 font-semibold text-white shadow hover:bg-indigo-700 transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            <AppleIcon className="text-white" />
            Download on App Store
          </a>
          <a
            href="https://play.google.com"
            className="inline-flex items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white px-6 py-3.5 font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            <GooglePlayIcon className="text-gray-700" />
            Get it on Google Play
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-gray-100 bg-white px-6 py-10">
      <div className="mx-auto max-w-6xl flex flex-col sm:flex-row items-center justify-between gap-4">
        <span className="text-lg font-bold text-indigo-600">Lambada</span>
        <nav className="flex flex-wrap gap-5 text-sm text-gray-500">
          <a href="/privacy" className="hover:text-indigo-600 transition-colors">Privacy Policy</a>
          <a href="/deleteaccount" className="hover:text-indigo-600 transition-colors">Delete Account</a>
          <a href="mailto:support@fibosolutions.com" className="hover:text-indigo-600 transition-colors">Contact</a>
        </nav>
        <p className="text-sm text-gray-400">&copy; {new Date().getFullYear()} Lambada</p>
      </div>
    </footer>
  );
}

function AppleIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={`h-5 w-5 ${className}`} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.3.07 2.2.74 2.96.76.87 0 2.51-.93 4.23-.79 2.37.18 3.71 1.34 3.71 1.34s-3.2 1.85-2.75 5.16c.4 2.82 2.83 3.83 2.83 3.83-.47 1.22-1.06 2.42-1.98 3.58zM13 3.5C13.73 2.67 14.94 2 16 2c.16 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.96 1.42-.18-1.13.44-2.3 1-3.11z" />
    </svg>
  );
}

function GooglePlayIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={`h-5 w-5 ${className}`} viewBox="0 0 24 24" fill="currentColor">
      <path d="M3.18 23.76c.32.18.68.23 1.04.14l12.02-6.94-2.63-2.63L3.18 23.76zM20.54 9.76L17.7 8.1l-2.97 2.97 2.97 2.97 2.87-1.65c.82-.47.82-1.67-.03-2.63zM2.02 1.26C1.89 1.48 1.8 1.74 1.8 2.04v19.92c0 .3.09.56.22.78l.09.08 11.16-11.16v-.26L2.11 1.18l-.09.08zM14.38 7.93L3.18.24C2.82.05 2.46 0 2.14.14l11.43 11.43 2.63-2.63-.09-.09-.1-.1-.57-.57-.48-.25-.58-.5z" />
    </svg>
  );
}
